import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Alert } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { useBeforeUnload } from '../../../../hooks/useBeforeUnload/useBeforeUnload';
import { makePatch } from '../../../../api/ajax';
import { Field } from '../../../../components/Form';
import { Modal, onHideHandler, renderShowConfirmationModal } from '../../../../components';
import { actions as customerConfigurationsActions } from '../../../../redux/customerConfigurations';

const LinkExpirationModal = ({ customerConfiguration, onModalClose, ...props }) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useBeforeUnload(hasUnsavedChanges);

  const dispatch = useDispatch();

  const initialValues = {
    photoCaptureLinkExpirationInHours: customerConfiguration?.photoCaptureLinkExpirationInHours ?? '',
  };

  const handleSubmit = ({ photoCaptureLinkExpirationInHours }, setStatus, resetForm) => {
    const value = photoCaptureLinkExpirationInHours === '' ? null : Number(photoCaptureLinkExpirationInHours);

    makePatch('customerConfigurations/current', { photoCaptureLinkExpirationInHours: value }).subscribe(
      ({ response }) => {
        dispatch(customerConfigurationsActions.loadCurrentCustomerConfigurationSuccess(response));
        setShowSuccessMessage('Link expiration has been changed!');
        resetForm();
        setHasUnsavedChanges(false);
      },
      ({ response: { error } }) => {
        setStatus(error);
        setHasUnsavedChanges(false);
      }
    );
  };

  return (
    <>
      {renderShowConfirmationModal(showConfirmModal, setShowConfirmModal, () => {
        setShowConfirmModal(false);
        onModalClose();
      })}
      <Modal
        headerText="Change default link expiration"
        size="md"
        backdrop="static"
        onHide={() => onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal)}
        {...props}
      >
        <Modal.Body>
          <div className="row">
            <div className="col">
              {showSuccessMessage && (
                <Alert variant="success" dismissible onClick={() => setShowSuccessMessage(null)}>
                  {showSuccessMessage}
                </Alert>
              )}
            </div>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object().shape({
              photoCaptureLinkExpirationInHours: Yup.number()
                .integer('Must be a whole number')
                .min(1, 'Must be at least 1')
                .nullable()
                .transform((value, original) => (original === '' ? null : value)),
            })}
            onSubmit={({ photoCaptureLinkExpirationInHours }, { setStatus, resetForm }) =>
              handleSubmit({ photoCaptureLinkExpirationInHours }, setStatus, resetForm)}
            validateOnChange={false}
          >
            {({ errors, status, touched }) => (
              <Form
                onChange={({ target }) => {
                  if (!hasUnsavedChanges && target.value !== String(initialValues[target.name])) {
                    setHasUnsavedChanges(true);
                  }
                }}
              >
                <div className="row">
                  <div className="col-12">
                    <Field
                      type="number"
                      min="1"
                      name="photoCaptureLinkExpirationInHours"
                      label="Hours"
                      placeholder="Off"
                      touched={touched.photoCaptureLinkExpirationInHours}
                      errors={errors.photoCaptureLinkExpirationInHours || status}
                    />
                  </div>
                </div>
                <div className="row mt-4">
                  <div className="col-12">
                    <button
                      type="reset"
                      className="btn btn-secondary mr-1"
                      onClick={() => onHideHandler(hasUnsavedChanges, onModalClose, setShowConfirmModal)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

LinkExpirationModal.propTypes = {
  onModalClose: PropTypes.func,
  customerConfiguration: PropTypes.object,
};

export default LinkExpirationModal;
